import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from '../../icons';
import { getDateLocale } from '../../lib/base-i18n';
import { tCalendar } from '../../lib/calendar-i18n';
import { DrumTimePicker } from './DrumTimePicker';

interface DateTimePickerProps {
  /** mode='datetime' → 'YYYY-MM-DDTHH:mm', mode='date' → 'YYYY-MM-DD'. */
  value: string;
  onChange: (value: string) => void;
  mode?: PickerMode;
  id?: string;
  required?: boolean;
  /** 트리거 좌측 액센트 색(이벤트 타입 색과 일치). 기본 primary. */
  accent?: string;
}

// 12시간제 시(자정 = 「오전 12」)·두 자리 채우기는 time-label.ts 하나로(2026-10-19 정리).
import { displayHour12, pad2 as pad } from './time-label';

/**
 * ⭐ 2026-10-16 — 'time'(시간만 고르는 피커)을 걷었다. 일정 창의 시작/종료가 다시 «한 칸»(달력 + 시간)이
 *   됐다(사용자 지시 「처음처럼」) — 그 두 칸 배치에서만 쓰던 모드다.
 */
type PickerMode = 'datetime' | 'date';


function parse(value: string, mode: PickerMode): { date: Date | null; h: number; m: number } {
  if (!value) return { date: null, h: 9, m: 0 };
  const [d, t] = value.split('T');
  const [y, mo, day] = (d ?? '').split('-').map(Number);
  if (!y || !mo || !day) return { date: null, h: 9, m: 0 };
  const date = new Date(y, mo - 1, day);
  if (mode === 'date' || !t) return { date, h: 0, m: 0 };
  const [h, m] = t.split(':').map(Number);
  return { date, h: h ?? 0, m: m ?? 0 };
}

function serialize(date: Date, h: number, m: number, mode: PickerMode): string {
  const ymd = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  return mode === 'date' ? ymd : `${ymd}T${pad(h)}:${pad(m)}`;
}

/**
 * DateTimePicker (v2.11.7) — 네이티브 datetime-local/date 대체 커스텀 피커.
 *
 * 기존 폼 상태 형식(datetime: 'YYYY-MM-DDTHH:mm', date: 'YYYY-MM-DD')을 그대로
 * 입출력하므로 부모는 input 한 줄만 교체하면 된다(데이터 모델 무변경).
 * 트리거 버튼 + 앵커드 팝오버(월 그리드 + 시/분 휠). 이모지 금지(인라인 SVG),
 * 날짜 표기는 getDateLocale SSOT. 바깥 클릭/Esc 로 닫힘, 뷰포트 clamp + 위로 플립.
 */
export function DateTimePicker({ value, onChange, mode = 'datetime', id, required, accent }: DateTimePickerProps) {
  const parsed = useMemo(() => parse(value, mode), [value, mode]);
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => parsed.date ?? new Date());
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; placement: 'down' | 'up' }>({ top: 0, left: 0, placement: 'down' });

  // 열릴 때 현재 값의 달로 뷰 이동.
  useEffect(() => {
    if (open && parsed.date) setViewMonth(new Date(parsed.date.getFullYear(), parsed.date.getMonth(), 1));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // 앵커드 포지셔닝 + clamp + 위로 플립.
  useLayoutEffect(() => {
    if (!open) return;
    const t = triggerRef.current;
    const el = popRef.current;
    if (!t || !el) return;
    const r = t.getBoundingClientRect();
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const margin = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = r.left;
    if (left + w + margin > vw) left = vw - w - margin;
    if (left < margin) left = margin;
    let top = r.bottom + 6;
    let placement: 'down' | 'up' = 'down';
    if (top + h + margin > vh) {
      top = Math.max(margin, r.top - h - 6);
      placement = 'up';
    }
    setPos({ top, left, placement });
  }, [open, viewMonth]);

  // 바깥 클릭/Esc.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (popRef.current?.contains(e.target as Node) || triggerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false); }
    window.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('mousedown', onDoc); window.removeEventListener('keydown', onKey); };
  }, [open]);

  const locale = getDateLocale();
  const triggerLabel = useMemo(() => {
    if (!parsed.date) return tCalendar('calendar.picker.placeholder');
    const dateStr = parsed.date.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' });
    if (mode === 'date') return dateStr;
    const ampm = parsed.h < 12 ? tCalendar('calendar.picker.am') : tCalendar('calendar.picker.pm');
    return `${dateStr} · ${ampm} ${displayHour12(parsed.h)}:${pad(parsed.m)}`;
  }, [parsed, mode, locale]);

  const dayNames = [
    tCalendar('calendar.month.dayname.sun'), tCalendar('calendar.month.dayname.mon'),
    tCalendar('calendar.month.dayname.tue'), tCalendar('calendar.month.dayname.wed'),
    tCalendar('calendar.month.dayname.thu'), tCalendar('calendar.month.dayname.fri'),
    tCalendar('calendar.month.dayname.sat'),
  ];

  const weeks = useMemo(() => buildMonth(viewMonth), [viewMonth]);
  const todayKey = dateKey(new Date());
  const selectedKey = parsed.date ? dateKey(parsed.date) : null;

  function pickDay(d: Date) {
    onChange(serialize(d, parsed.h, parsed.m, mode));
    if (mode === 'date') setOpen(false);
  }
  function pickTime(h: number, m: number) {
    const base = parsed.date ?? new Date();
    onChange(serialize(base, h, m, mode));
  }

  const monthLabel = viewMonth.toLocaleDateString(locale, { year: 'numeric', month: 'long' });
  const accentColor = accent ?? 'var(--primary)';

  return (
    <div className="cal-dtp">
      <button
        type="button"
        ref={triggerRef}
        id={id}
        className={`cal-dtp-trigger${open ? ' cal-dtp-trigger--open' : ''}${!parsed.date ? ' cal-dtp-trigger--empty' : ''}`}
        style={{ '--dtp-accent': accentColor } as React.CSSProperties}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-required={required}
        aria-label={tCalendar('calendar.picker.aria')}
      >
        <span className="cal-dtp-trigger-ico" aria-hidden>
          <CalendarIcon size={15} />
        </span>
        <span className="cal-dtp-trigger-text">{triggerLabel}</span>
        <span className="cal-dtp-trigger-chev" aria-hidden><ChevronRight size={14} /></span>
      </button>

      {open && (
        <div
          ref={popRef}
          className={`cal-dtp-pop cal-dtp-pop--${pos.placement}`}
          role="dialog"
          aria-label={tCalendar('calendar.picker.aria')}
          style={{ top: pos.top, left: pos.left }}
        >
          <div className="cal-dtp-cal">
            <div className="cal-dtp-cal-head">
              <button type="button" className="cal-dtp-navbtn" aria-label={tCalendar('calendar.nav.prev_month')}
                onClick={() => setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}>
                <ChevronLeft size={16} />
              </button>
              <span className="cal-dtp-month">{monthLabel}</span>
              <button type="button" className="cal-dtp-navbtn" aria-label={tCalendar('calendar.nav.next_month')}
                onClick={() => setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}>
                <ChevronRight size={16} />
              </button>
            </div>
            <div className="cal-dtp-dow">
              {dayNames.map((d, i) => (
                <span key={d} className={`cal-dtp-dow-cell${i === 0 ? ' cal-dtp-dow-cell--sun' : i === 6 ? ' cal-dtp-dow-cell--sat' : ''}`}>{d}</span>
              ))}
            </div>
            <div className="cal-dtp-grid">
              {weeks.map((d, i) => {
                const k = dateKey(d);
                const other = d.getMonth() !== viewMonth.getMonth();
                const dow = d.getDay();
                const isSel = k === selectedKey;
                const isToday = k === todayKey;
                return (
                  <button
                    key={i}
                    type="button"
                    className={[
                      'cal-dtp-day',
                      other ? 'cal-dtp-day--other' : '',
                      isSel ? 'cal-dtp-day--sel' : '',
                      isToday ? 'cal-dtp-day--today' : '',
                      dow === 0 ? 'cal-dtp-day--sun' : dow === 6 ? 'cal-dtp-day--sat' : '',
                    ].filter(Boolean).join(' ')}
                    style={{ animationDelay: `${Math.floor(i / 7) * 24}ms` }}
                    onClick={() => pickDay(d)}
                  >
                    {d.getDate()}
                  </button>
                );
              })}
            </div>

            {mode === 'date' && (
              <div className="cal-dtp-date-foot">
                <button type="button" className="cal-dtp-done" onClick={() => setOpen(false)}>
                  {tCalendar('calendar.picker.confirm')}
                </button>
              </div>
            )}
          </div>

          {mode !== 'date' && (
            <div className="cal-dtp-time">
              {/* ⭐ 2026-10-16 — 시간 칸은 돌아가는 드럼(오전/오후 · 1~12시 · 분). 값은 그대로 24시간제로 담는다. */}
              <DrumTimePicker
                hour={parsed.h}
                minute={parsed.m}
                onChange={pickTime}
              >
              <div className="cal-dtp-time-foot">
                <button type="button" className="cal-dtp-done" onClick={() => setOpen(false)}>
                  {tCalendar('calendar.picker.confirm')}
                </button>
              </div>
              </DrumTimePicker>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function dateKey(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** 해당 월의 6주(42칸) 그리드 — 앞뒤 달 채움. */
function buildMonth(viewMonth: Date): Date[] {
  const first = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}
